// Mock offer generators for the demo airlines (MOVMO-373).
//
// In mock mode (?mock=true) the demo pages skip their real provider calls
// (Darwin for Aero; the Amadeus-shaped PSS payload for Philippine Airlines
// and SAS — the Amadeus API itself was retired in MOVMO-590) and use these
// generators instead. The shapes returned here match what the airlines'
// existing UI components expect, so result/checkout pages don't need to
// branch on mock mode beyond the data-source switch.
//
// At the MovmoCheckoutButton handoff, the demos call buildMovmoFlightOffer*
// to produce the canonical MovmoFlightOffer shape that the backend's
// mock-aero / mock-pa adapter persists round-tripping.

import type {
    DarwinTrip,
    DarwinFareClass,
    DarwinFare,
    DarwinPtcFare,
    AeroSelectedFlight,
} from "../types/darwin";
import type {
    FlightOffer,
    AmadeusDictionaries,
    AmadeusFlightSearchResponse,
} from "../types/flight";

// MovmoFlightOffer subset matching go-services-core/models/flights.go.
// Only the fields the mock backend's GetOffer round-trips through JSON
// are included — flights-ui will fill in the rest from defaults.
export interface MovmoFlightOfferLike {
    id: string;
    provider_id: string;
    total_amount: string;
    total_currency: string;
    base_amount: string;
    base_currency: string;
    tax_amount: string;
    tax_currency: string;
    expires_at: string;
    owner: { id: string; name: string; iata_code: string };
    passengers: Array<{
        id: string;
        provider_passenger_id: string;
        type: string;
    }>;
    slices: Array<{
        id: string;
        origin_type: "airport";
        origin: MovmoPlaceLike;
        destination_type: "airport";
        destination: MovmoPlaceLike;
        segments: MovmoSegmentLike[];
        // models.Duration in Go marshals as "X hr Y min" — match that format
        // so flights-ui can re-parse it on the receiving side.
        duration?: string;
        fare_brand_name?: string;
    }>;
    conditions: {
        refunds_before_departure: { allowed: boolean };
        changes_before_departure: { allowed: boolean };
    };
    payment_requirements: { requires_instant_payment: boolean };
}

interface MovmoPlaceLike {
    id: string;
    type: "airport";
    time_zone: string;
    name: string;
    iata_code: string;
    iata_city_code: string;
    city_name: string;
    iata_country_code: string;
}

interface MovmoSegmentLike {
    id: string;
    origin: MovmoPlaceLike;
    destination: MovmoPlaceLike;
    departing_at: string; // ISO without TZ (LocalTime in Go)
    arriving_at: string;
    duration?: string; // "X hr Y min" — matches models.Duration JSON shape
    // Ground time between THIS segment's arrival and the next segment's
    // departure. flights-ui's FlightSummary renders its "layover • airport"
    // row from the segment BEFORE the stop (and ignores it on the last
    // segment), so set it on the arriving-into-the-connection segment.
    layover_duration?: string;
    marketing_carrier: { id: string; name: string; iata_code: string };
    marketing_carrier_flight_number: string;
    operating_carrier: { id: string; name: string; iata_code: string };
    operating_carrier_flight_number: string;
    aircraft?: { id: string; name: string; iata_code: string };
    passengers?: Array<{
        passenger_id: string;
        cabin_class: string;
        cabin_class_marketing_name?: string;
        // The cabin object lets flights-ui render the proper-case cabin
        // name in the inline summary (e.g. "First" instead of the
        // lowercase enum value "first" from cabin_class).
        cabin?: { name: string; marketing_name?: string };
    }>;
}

// formatDuration renders a minute count as "X hr Y min" — the format
// models.Duration's JSON UnmarshalJSON expects on the Go side, so the
// stored offer round-trips through GetOffer cleanly.
function formatDuration(totalMinutes: number): string {
    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0 hr 0 min";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} hr ${minutes} min`;
}

// Aero's actual fleet (https://aero.com/fleet). Use ERJ-135 as the default
// for short/medium routes — it's the workhorse for Aero's commercial-style
// scheduled service.
const AERO_AIRCRAFT = {
    id: "mock-aircraft-erj135",
    name: "Embraer ERJ-135",
    iata_code: "ER3",
};

// Philippine Airlines' typical short-haul aircraft.
const PA_AIRCRAFT = {
    id: "mock-aircraft-a321neo",
    name: "Airbus A321neo",
    iata_code: "32Q",
};

const pad = (n: number) => String(n).padStart(2, "0");
const isoNoTZ = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours(),
    )}:${pad(d.getMinutes())}:00`;

// Airport metadata for the airports the Aero and PA demos actually use.
// Keeps the MovmoFlightOffer shipped to the backend consistent with what the
// demo's search form and result/checkout pages display, so when flights-ui
// renders the offer back the city names and countries match the demo's UI.
// Fall back to deriving values from the IATA code for any unknown airport.
const AIRPORT_METADATA: Record<
    string,
    { city: string; name: string; country: string; tz: string }
> = {
    // Aero routes (mirrors AERO_AIRPORTS in darwinApi.ts)
    MCO: { city: "Orlando", name: "Orlando International Airport", country: "US", tz: "America/New_York" },
    JFK: { city: "New York", name: "John F. Kennedy International Airport", country: "US", tz: "America/New_York" },
    LAS: { city: "Las Vegas", name: "Harry Reid International Airport", country: "US", tz: "America/Los_Angeles" },
    VNY: { city: "Los Angeles", name: "Van Nuys Airport", country: "US", tz: "America/Los_Angeles" },
    SFO: { city: "San Francisco", name: "San Francisco International Airport", country: "US", tz: "America/Los_Angeles" },
    DFW: { city: "Dallas", name: "Dallas Fort Worth International Airport", country: "US", tz: "America/Chicago" },
    ASE: { city: "Aspen", name: "Aspen/Pitkin County Airport", country: "US", tz: "America/Denver" },
    MIA: { city: "Miami", name: "Miami International Airport", country: "US", tz: "America/New_York" },
    EGE: { city: "Vail", name: "Eagle County Regional Airport", country: "US", tz: "America/Denver" },
    SUN: { city: "Sun Valley", name: "Friedman Memorial Airport", country: "US", tz: "America/Denver" },
    PTY: { city: "Panama City", name: "Tocumen International Airport", country: "PA", tz: "America/Panama" },
    // Philippine Airlines routes (mirrors the PA cityMap in ShoppingCartPage)
    MNL: { city: "Manila", name: "Ninoy Aquino International Airport", country: "PH", tz: "Asia/Manila" },
    CEB: { city: "Cebu", name: "Mactan-Cebu International Airport", country: "PH", tz: "Asia/Manila" },
    DVO: { city: "Davao", name: "Francisco Bangoy International Airport", country: "PH", tz: "Asia/Manila" },
    // Clic (Colombia) routes — KIU demo (MOVMO-511)
    BOG: { city: "Bogotá", name: "El Dorado International Airport", country: "CO", tz: "America/Bogota" },
    CLO: { city: "Cali", name: "Alfonso Bonilla Aragón International Airport", country: "CO", tz: "America/Bogota" },
    EOH: { city: "Medellín", name: "Olaya Herrera Airport", country: "CO", tz: "America/Bogota" },
    // Common PA destinations
    LAX: { city: "Los Angeles", name: "Los Angeles International Airport", country: "US", tz: "America/Los_Angeles" },
    SIN: { city: "Singapore", name: "Singapore Changi Airport", country: "SG", tz: "Asia/Singapore" },
    HKG: { city: "Hong Kong", name: "Hong Kong International Airport", country: "HK", tz: "Asia/Hong_Kong" },
    NRT: { city: "Tokyo", name: "Narita International Airport", country: "JP", tz: "Asia/Tokyo" },
    HND: { city: "Tokyo", name: "Tokyo Haneda Airport", country: "JP", tz: "Asia/Tokyo" },
    SYD: { city: "Sydney", name: "Sydney Kingsford Smith Airport", country: "AU", tz: "Australia/Sydney" },
};

function buildPlace(iata: string): MovmoPlaceLike {
    const upper = iata.toUpperCase();
    const meta = AIRPORT_METADATA[upper];
    return {
        id: `mock-place-${upper}`,
        type: "airport",
        time_zone: meta?.tz ?? "UTC",
        name: meta?.name ?? `${upper} International Airport`,
        iata_code: upper,
        iata_city_code: upper,
        city_name: meta?.city ?? upper,
        iata_country_code: meta?.country ?? "US",
    };
}

interface VariantSpec {
    label: string;
    brand: string;
    departHour: number;
    departMinute: number;
    durationMinutes: number;
    basePrice: number; // in dollars (whole units, e.g. 189)
}

const VARIANTS: VariantSpec[] = [
    { label: "Saver", brand: "economy_basic", departHour: 6, departMinute: 15, durationMinutes: 195, basePrice: 189 },
    { label: "Standard", brand: "economy_standard", departHour: 10, departMinute: 30, durationMinutes: 180, basePrice: 245 },
    { label: "Flex", brand: "economy_flex", departHour: 14, departMinute: 45, durationMinutes: 200, basePrice: 278 },
    { label: "Plus", brand: "premium_economy", departHour: 16, departMinute: 20, durationMinutes: 215, basePrice: 410 },
];

// Returns the date with the variant's intended local departure time applied.
function variantDepartureDate(baseDate: Date, v: VariantSpec): Date {
    const d = new Date(baseDate);
    d.setHours(v.departHour, v.departMinute, 0, 0);
    return d;
}

// ---- Darwin (Aero) shape ---------------------------------------------------

export interface MockAeroSearchInput {
    origin: string;
    destination: string;
    departureDate: string; // YYYY-MM-DD
    adults: number;
}

// Aero is a private charter operator — every seat is effectively first
// class, sold under a single product called "Aero Select". The variant
// list below produces multiple departure times at different price points
// (driven by demand / aircraft availability), but they are NOT differentiated
// by cabin class or fare brand the way a commercial carrier's Saver / Flex
// / Premium tiers would be.
export function buildMockAeroTrips(input: MockAeroSearchInput): DarwinTrip[] {
    const baseDate = new Date(`${input.departureDate}T00:00:00`);
    return VARIANTS.map((v, i) => {
        const dep = variantDepartureDate(baseDate, v);
        const arr = new Date(dep.getTime() + v.durationMinutes * 60 * 1000);
        const ptcFare: DarwinPtcFare = {
            sortIndex: 0,
            currencyCode: "USD",
            ptcCode: "ADT",
            baseFare: v.basePrice * 0.85,
            totalFare: v.basePrice,
            ptcTaxes: [
                {
                    taxCode: "US",
                    refundable: false,
                    amount: v.basePrice * 0.15,
                },
            ],
        };
        const fare: DarwinFare = {
            fareSummaryUid: `mock-aero-fare-${i}`,
            fareBasisCode: "AEROSELECT",
            isRoundTripFare: false,
            hasCombinabilityRule: false,
            fareBrandUid: `mock-aero-brand-${i}`,
            privateFare: true, // Aero is a private charter
            ptcFares: [ptcFare],
        };
        const fareClass: DarwinFareClass = {
            // F (first) for cabin and class — Aero has no economy cabin.
            fareClassCode: "F",
            cabinCode: "F",
            avail: 9,
            infantAvail: 9,
            fares: [fare],
        };
        return {
            availabilityTripUid: `mock-aero-trip-${i}`,
            availabilityRequestDetailIndex: 0,
            tripFlight: {
                tripFlightUid: `mock-aero-tripflight-${i}`,
                origin: input.origin,
                destination: input.destination,
                departureDate: dep.toISOString(),
                marketingCarrierCode: "ZE",
                flightNumber: String(100 + i),
                departureTime: `${pad(dep.getHours())}:${pad(dep.getMinutes())}`,
                arrivalTime: `${pad(arr.getHours())}:${pad(arr.getMinutes())}`,
                arrivalDateAdjustor: 0,
                duration: v.durationMinutes,
                numStops: 0,
                flightNote: "",
                region: "USA",
                flightViews: [],
            },
            fareClasses: [fareClass],
        };
    });
}

// Builds the canonical MovmoFlightOffer the Express Checkout button POSTs
// to /v1/flights/providers/mock-aero/offers. The backend mock adapter
// persists this verbatim and parses it back on GetOffer.
export function buildMovmoFlightOfferForAero(
    selected: AeroSelectedFlight,
    returnFlight: AeroSelectedFlight | null,
    counts: { adults: number; infants: number },
): MovmoFlightOfferLike {
    // The MovmoCarrier.iata_code is what flights-ui combines with the
    // segment flight number to render "Aero 100". Use the capitalized
    // brand label so the inline display reads correctly (the lowercase
    // "aero" slug is reserved for the movmo-customer-id header on the
    // proxy request, not for the offer's carrier identity).
    const owner = { id: "mock-carrier-aero", name: "Aero", iata_code: "Aero" };
    // MovmoPassenger.id is the saved user_passenger UUID — leave it empty
    // until flights-ui creates the user_passenger row on submit. Mirrors the
    // Amadeus convention (models/amadeus/conversions.go), which sets ID:""
    // and uses provider_passenger_id as the segment back-reference. Without
    // this, IndividualPassengerCard sees a non-empty id, takes the "update"
    // branch, and PUTs /v1/users/{userId}/passengers/mock-pax-1 → 400.
    const passengers: MovmoFlightOfferLike["passengers"] = [];
    let idx = 1;
    const addPax = (type: string, n: number) => {
        for (let i = 0; i < n; i++) {
            passengers.push({
                id: "",
                provider_passenger_id: String(idx),
                type,
            });
            idx++;
        }
    };
    addPax("adult", counts.adults || 1);
    addPax("infant_lap", counts.infants);

    const totalAmount =
        selected.ptcFare.totalFare * (counts.adults || 1) +
        (returnFlight?.ptcFare.totalFare || 0) * (counts.adults || 1);
    const baseAmount = totalAmount * 0.85;

    const sliceFromSelection = (
        s: AeroSelectedFlight,
        sliceID: string,
        segID: string,
    ): MovmoFlightOfferLike["slices"][number] => {
        const tf = s.trip.tripFlight;
        const dep = new Date(tf.departureDate);
        const durationMinutes = tf.duration || 180;
        const arr = new Date(dep.getTime() + durationMinutes * 60 * 1000);
        const durationStr = formatDuration(durationMinutes);
        // Aero is a private charter operator. Every seat is first class,
        // sold under one product called "Aero Select". Hard-code both the
        // brand and the cabin so the offer renders the right chip and
        // cabin label in flights-ui regardless of which variant the user
        // picked from the result list. The cabin object carries the
        // proper-case display name flights-ui shows in the inline
        // summary (otherwise the cabin_class enum value "first" appears
        // verbatim, lowercase).
        const fareBrand = "Aero Select";
        const cabin = "first";
        const cabinDisplay = { name: "First", marketing_name: fareBrand };
        const segPassengers = passengers.map((p) => ({
            passenger_id: p.provider_passenger_id,
            cabin_class: cabin,
            cabin_class_marketing_name: fareBrand,
            cabin: cabinDisplay,
        }));
        return {
            id: sliceID,
            origin_type: "airport",
            origin: buildPlace(tf.origin),
            destination_type: "airport",
            destination: buildPlace(tf.destination),
            duration: durationStr,
            fare_brand_name: fareBrand,
            segments: [
                {
                    id: segID,
                    origin: buildPlace(tf.origin),
                    destination: buildPlace(tf.destination),
                    departing_at: isoNoTZ(dep),
                    arriving_at: isoNoTZ(arr),
                    duration: durationStr,
                    marketing_carrier: owner,
                    marketing_carrier_flight_number: tf.flightNumber,
                    operating_carrier: owner,
                    operating_carrier_flight_number: tf.flightNumber,
                    aircraft: AERO_AIRCRAFT,
                    passengers: segPassengers,
                },
            ],
        };
    };

    const slices = [sliceFromSelection(selected, "mock-slice-out", "mock-seg-out")];
    if (returnFlight) {
        slices.push(
            sliceFromSelection(returnFlight, "mock-slice-ret", "mock-seg-ret"),
        );
    }

    return {
        id: "mock-aero-pending",
        provider_id: "mock-aero",
        total_amount: totalAmount.toFixed(2),
        total_currency: "USD",
        base_amount: baseAmount.toFixed(2),
        base_currency: "USD",
        tax_amount: (totalAmount - baseAmount).toFixed(2),
        tax_currency: "USD",
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        owner,
        passengers,
        slices,
        conditions: {
            refunds_before_departure: { allowed: false },
            changes_before_departure: { allowed: false },
        },
        payment_requirements: { requires_instant_payment: true },
    };
}

// ---- Clic (KIU) shape -------------------------------------------------------

// Clic's regional fleet (ex-EasyFly) is ATR 72-600 turboprops.
const CLIC_AIRCRAFT = {
    id: "mock-aircraft-atr72",
    name: "ATR 72-600",
    iata_code: "AT7",
};

// Builds the fixed offer for the Clic screenshot demo (MOVMO-511): one-way
// BOG → CLO on 2026-07-17, 1 adult, ECONOMICA — the itinerary selected in the
// Figma screenshots the demo displays (Movmo-Desktop-Booking-Flow_Master,
// frame 5599:107553: the 05:20 departure, "1 conexión", 8h 15min).
//
// The Clic screens show 832.900,00 COP; the Movmo checkout renders this offer
// in USD at a fixed conversion of 3,250 COP/USD (rate on 2026-07-12) →
// $256.28. There is no live search step — the demo is screenshot-driven — so
// unlike the Aero/PA builders this takes no selection input.
export function buildMovmoFlightOfferForClic(): MovmoFlightOfferLike {
    const owner = { id: "mock-carrier-ve", name: "Clic", iata_code: "VE" };
    // id stays empty until flights-ui creates the user_passenger row — see
    // the note in buildMovmoFlightOfferForAero.
    const passengers: MovmoFlightOfferLike["passengers"] = [
        { id: "", provider_passenger_id: "1", type: "adult" },
    ];

    const totalAmount = 256.28; // 832,900 COP @ 3,250 COP/USD (2026-07-12)
    const baseAmount = totalAmount * 0.85;

    const cabin = "economy";
    const fareBrand = "Economica";
    const cabinDisplay = { name: "Economy", marketing_name: fareBrand };
    const segPassengers = passengers.map((p) => ({
        passenger_id: p.provider_passenger_id,
        cabin_class: cabin,
        cabin_class_marketing_name: fareBrand,
        cabin: cabinDisplay,
    }));

    const segment = (
        id: string,
        origin: string,
        destination: string,
        departingAt: string,
        arrivingAt: string,
        durationMinutes: number,
        flightNumber: string,
        layoverMinutes?: number,
    ): MovmoSegmentLike => ({
        id,
        origin: buildPlace(origin),
        destination: buildPlace(destination),
        departing_at: departingAt,
        arriving_at: arrivingAt,
        duration: formatDuration(durationMinutes),
        ...(layoverMinutes ? { layover_duration: formatDuration(layoverMinutes) } : {}),
        marketing_carrier: owner,
        marketing_carrier_flight_number: flightNumber,
        operating_carrier: owner,
        operating_carrier_flight_number: flightNumber,
        aircraft: CLIC_AIRCRAFT,
        passengers: segPassengers,
    });

    return {
        id: "mock-clic-pending",
        provider_id: "mock-clic",
        total_amount: totalAmount.toFixed(2),
        total_currency: "USD",
        base_amount: baseAmount.toFixed(2),
        base_currency: "USD",
        tax_amount: (totalAmount - baseAmount).toFixed(2),
        tax_currency: "USD",
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        owner,
        passengers,
        slices: [
            {
                id: "mock-slice-out",
                origin_type: "airport",
                origin: buildPlace("BOG"),
                destination_type: "airport",
                destination: buildPlace("CLO"),
                duration: formatDuration(8 * 60 + 15),
                fare_brand_name: fareBrand,
                segments: [
                    // "1 conexión" via Medellín, matching the selected row's
                    // 05:20 → 13:35 endpoints on the results screenshot. The
                    // 6h20 ground time is what the Clic fare actually implies
                    // (8h15 total for ~2h of flying); layover_duration on the
                    // first segment makes flights-ui render it as an explicit
                    // "layover • Olaya Herrera (EOH)" row instead of two
                    // seemingly unrelated segments.
                    segment("mock-seg-out-1", "BOG", "EOH", "2026-07-17T05:20:00", "2026-07-17T06:15:00", 55, "205", 6 * 60 + 20),
                    segment("mock-seg-out-2", "EOH", "CLO", "2026-07-17T12:35:00", "2026-07-17T13:35:00", 60, "311"),
                ],
            },
        ],
        conditions: {
            refunds_before_departure: { allowed: false },
            changes_before_departure: { allowed: false },
        },
        payment_requirements: { requires_instant_payment: true },
    };
}

// ---- Amadeus-shaped PSS payload (Philippine Airlines, SAS) -----------------
//
// The shape below is the airline-PSS "flight-offer" payload format the
// Express Checkout button accepts as a data source. It is NOT a call to the
// Amadeus API (removed in MOVMO-590); both the PA and SAS demos synthesise
// offers in this shape client-side.

export interface MockPaSearchInput {
    origin: string;
    destination: string;
    departureDate: string; // YYYY-MM-DD
    returnDate?: string; // YYYY-MM-DD
    adults: number;
    children: number;
    infants: number;
    travelClass?: string;
}

const PA_OWNER = "PR";

interface AmadeusShapeOpts {
    carrier: string; // e.g. "PR", "SK"
    carrierName: string;
    currency: string;
    idPrefix: string; // e.g. "mock-pa", "mock-sas" — also the segment id seed
    originCountry: string;
    destinationCountry: string;
    // When set, every offer is stamped with this cabin instead of the
    // per-variant default. Used by the SAS multi-class search.
    cabin?: "ECONOMY" | "PREMIUM_ECONOMY" | "BUSINESS";
    priceMultiplier?: number;
    offerIdSuffix?: string;
}

function amadeusDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `PT${h}H${m}M`;
}

function buildAmadeusItinerary(
    origin: string,
    destination: string,
    baseDate: Date,
    v: VariantSpec,
    segIDPrefix: string,
    carrier: string = PA_OWNER,
) {
    const dep = variantDepartureDate(baseDate, v);
    const arr = new Date(dep.getTime() + v.durationMinutes * 60 * 1000);
    return {
        duration: amadeusDuration(v.durationMinutes),
        segments: [
            {
                departure: { iataCode: origin, at: isoNoTZ(dep) },
                arrival: { iataCode: destination, at: isoNoTZ(arr) },
                carrierCode: carrier,
                number: String(100 + (segIDPrefix.length % 900)),
                duration: amadeusDuration(v.durationMinutes),
                aircraft: { code: "738" },
                operating: { carrierCode: carrier },
                id: segIDPrefix,
                numberOfStops: 0,
                blacklistedInEU: false,
            },
        ],
    };
}

export function buildMockPaResponse(
    input: MockPaSearchInput,
): AmadeusFlightSearchResponse {
    return buildAmadeusShapedResponse(input, {
        carrier: PA_OWNER,
        carrierName: "Philippine Airlines",
        currency: "USD",
        idPrefix: "mock-pa",
        originCountry: "PH",
        destinationCountry: "US",
    });
}

export interface MockSasMultiClassResponse {
    economy: FlightOffer[];
    premiumEconomy: FlightOffer[];
    business: FlightOffer[];
    dictionaries: AmadeusDictionaries;
    meta: { count: number };
}

// SAS multi-cabin search: the same flights priced in three cabins. The
// segment keys (carrier + number + route + departure time) are identical
// across the three arrays so utils/flightMatcher.groupFlightsByRoute can
// line them up into one row per flight.
export function buildMockSasMultiClassResponse(
    input: MockPaSearchInput,
): MockSasMultiClassResponse {
    const base = {
        carrier: "SK",
        carrierName: "Scandinavian Airlines",
        currency: "EUR",
        idPrefix: "mock-sas",
        originCountry: "NO",
        destinationCountry: "US",
    };
    const economy = buildAmadeusShapedResponse(input, {
        ...base, cabin: "ECONOMY", priceMultiplier: 1, offerIdSuffix: "eco",
    });
    const premiumEconomy = buildAmadeusShapedResponse(input, {
        ...base, cabin: "PREMIUM_ECONOMY", priceMultiplier: 1.6, offerIdSuffix: "pre",
    });
    const business = buildAmadeusShapedResponse(input, {
        ...base, cabin: "BUSINESS", priceMultiplier: 2.8, offerIdSuffix: "bus",
    });
    return {
        economy: economy.data,
        premiumEconomy: premiumEconomy.data,
        business: business.data,
        dictionaries: economy.dictionaries ?? {},
        meta: { count: economy.data.length },
    };
}

function buildAmadeusShapedResponse(
    input: MockPaSearchInput,
    opts: AmadeusShapeOpts,
): AmadeusFlightSearchResponse {
    const owner = opts.carrier;
    const currency = opts.currency;
    const mult = opts.priceMultiplier ?? 1;
    const idSuffix = opts.offerIdSuffix ? `-${opts.offerIdSuffix}` : "";
    const baseDeparture = new Date(`${input.departureDate}T00:00:00`);
    const baseReturn = input.returnDate
        ? new Date(`${input.returnDate}T00:00:00`)
        : null;

    const totalPax =
        (input.adults || 1) + (input.children || 0) + (input.infants || 0);

    const offers: FlightOffer[] = VARIANTS.map((v, i) => {
        const itineraries = [
            buildAmadeusItinerary(input.origin, input.destination, baseDeparture, v, `${opts.idPrefix}-out-${i}`, owner),
        ];
        if (baseReturn) {
            itineraries.push(
                buildAmadeusItinerary(input.destination, input.origin, baseReturn, v, `${opts.idPrefix}-ret-${i}`, owner),
            );
        }
        const total = (v.basePrice * mult * totalPax * (baseReturn ? 2 : 1)).toFixed(2);
        const base = (v.basePrice * mult * totalPax * (baseReturn ? 2 : 1) * 0.85).toFixed(2);
        const cabin =
            opts.cabin ?? (v.brand === "premium_economy" ? "PREMIUM_ECONOMY" : "ECONOMY");

        return {
            id: `${opts.idPrefix}${idSuffix}-${i}`,
            source: "GDS",
            instantTicketingRequired: false,
            nonHomogeneous: false,
            oneWay: !baseReturn,
            lastTicketingDate: input.departureDate,
            numberOfBookableSeats: 9,
            itineraries,
            price: {
                currency,
                total,
                base,
                fees: [],
                grandTotal: total,
            },
            travelerPricings: Array.from({ length: totalPax }).map((_, p) => ({
                travelerId: String(p + 1),
                fareOption: "STANDARD",
                travelerType: p < (input.adults || 1) ? "ADULT" : "INFANT",
                price: { currency, total, base },
                fareDetailsBySegment: itineraries.flatMap((it) =>
                    it.segments.map((seg) => ({
                        segmentId: seg.id,
                        cabin,
                        fareBasis: v.brand.toUpperCase(),
                        brandedFare: v.label.toUpperCase(),
                        brandedFareLabel: v.label,
                        class: "Y",
                    })),
                ),
            })),
            validatingAirlineCodes: [owner],
        } as FlightOffer;
    });

    const dictionaries: AmadeusDictionaries = {
        carriers: { [owner]: opts.carrierName },
        locations: {
            [input.origin]: { cityCode: input.origin, countryCode: opts.originCountry },
            [input.destination]: { cityCode: input.destination, countryCode: opts.destinationCountry },
        },
        aircraft: { "738": "BOEING 737-800" },
        currencies: { [currency]: currency === "EUR" ? "EURO" : "US DOLLAR" },
    };

    return { data: offers, dictionaries };
}

// Builds the canonical MovmoFlightOffer the Express Checkout button POSTs
// to /v1/flights/providers/mock-pa/offers.
export function buildMovmoFlightOfferForPa(
    flight: FlightOffer,
    counts: { adults: number; children: number; infants: number },
    ownerOverride?: { id: string; name: string; iata_code: string },
): MovmoFlightOfferLike {
    const owner = ownerOverride ?? {
        id: "mock-carrier-pr",
        name: "Philippine Airlines",
        iata_code: "PR",
    };
    // See buildMovmoFlightOfferForAero — id is the saved user_passenger
    // UUID (empty until linked); provider_passenger_id is the stable
    // back-reference used by segment.passengers[].passenger_id.
    const passengers: MovmoFlightOfferLike["passengers"] = [];
    let idx = 1;
    const addPax = (type: string, n: number) => {
        for (let i = 0; i < n; i++) {
            passengers.push({
                id: "",
                provider_passenger_id: String(idx),
                type,
            });
            idx++;
        }
    };
    addPax("adult", counts.adults || 1);
    addPax("child", counts.children);
    addPax("infant_lap", counts.infants);

    // Pull the fare brand from the offer's first traveler pricing — the
    // PSS-shaped offer exposes brandedFareLabel ("Saver", "Standard", "Flex", "Plus" in our
    // mock) which is what the demo shows in the result card and what the
    // checkout overlay should display in the brand chip.
    const firstFareDetails =
        flight.travelerPricings?.[0]?.fareDetailsBySegment?.[0];
    const fareBrand =
        firstFareDetails?.brandedFareLabel ||
        firstFareDetails?.brandedFare ||
        "Standard";
    // PA's premium-economy fare in the mock is the "Plus" brand.
    const cabin =
        firstFareDetails?.cabin === "PREMIUM_ECONOMY"
            ? "premium_economy"
            : firstFareDetails?.cabin === "BUSINESS"
              ? "business"
              : "economy";
    // Proper-case cabin display name for the inline summary (otherwise
    // flights-ui shows the lowercase enum value).
    const cabinDisplayName =
        cabin === "premium_economy"
            ? "Premium Economy"
            : cabin === "business"
              ? "Business"
              : "Economy";
    const cabinDisplay = {
        name: cabinDisplayName,
        marketing_name: fareBrand,
    };

    const slices = flight.itineraries.map((it, sliceIdx) => {
        const seg = it.segments[0];
        const dep = new Date(seg.departure.at);
        const arr = new Date(seg.arrival.at);
        const segMinutes = Math.max(
            1,
            Math.round((arr.getTime() - dep.getTime()) / 60000),
        );
        const durationStr = formatDuration(segMinutes);
        const segPassengers = passengers.map((p) => ({
            passenger_id: p.provider_passenger_id,
            cabin_class: cabin,
            cabin_class_marketing_name: fareBrand,
            cabin: cabinDisplay,
        }));
        return {
            id: `mock-pa-slice-${sliceIdx}`,
            origin_type: "airport" as const,
            origin: buildPlace(seg.departure.iataCode),
            destination_type: "airport" as const,
            destination: buildPlace(seg.arrival.iataCode),
            duration: durationStr,
            fare_brand_name: fareBrand,
            segments: [
                {
                    id: `mock-pa-seg-${sliceIdx}`,
                    origin: buildPlace(seg.departure.iataCode),
                    destination: buildPlace(seg.arrival.iataCode),
                    departing_at: isoNoTZ(dep),
                    arriving_at: isoNoTZ(arr),
                    duration: durationStr,
                    marketing_carrier: owner,
                    marketing_carrier_flight_number: seg.number,
                    operating_carrier: owner,
                    operating_carrier_flight_number: seg.number,
                    aircraft: PA_AIRCRAFT,
                    passengers: segPassengers,
                },
            ],
        };
    });

    const total = parseFloat(flight.price.total);
    return {
        id: "mock-pa-pending",
        provider_id: "mock-pa",
        total_amount: total.toFixed(2),
        total_currency: flight.price.currency,
        base_amount: parseFloat(flight.price.base).toFixed(2),
        base_currency: flight.price.currency,
        tax_amount: (total - parseFloat(flight.price.base)).toFixed(2),
        tax_currency: flight.price.currency,
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        owner,
        passengers,
        slices,
        conditions: {
            refunds_before_departure: { allowed: true },
            changes_before_departure: { allowed: true },
        },
        payment_requirements: { requires_instant_payment: true },
    };
}
